import { FunctionComponent, useState } from 'react'
import {
  rucksack,
  Box,
  IconButton,
  Link,
  TableCell,
  TableRow,
} from '@black-bear-energy/rucksack'
import { RFPDocumentSummary } from '@black-bear-energy/black-bear-energy-common'
import { ExpandMore } from '@mui/icons-material'
import { Collapse } from '@mui/material'
import { DateAndTime } from './DateAndTime'
import { ReviewsIcon } from './ReviewsIcon'
import { SiteRFPTable } from './SiteRFPTable'
import { RequiredDocumentsProgressBar } from './RequiredDocumentsProgressBar'
import { TableCellWithDefault } from '../common/TableCellWithDefault'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { PodMemberButton } from './PodMemberButton'
import {
  CUSTOMER_RFP_COLUMNS,
  SITE_RFP_TABLE_PADDING,
} from '../helpers/DocumentExchange/tableConstants'
import { formatSystemSize } from '../helpers/formatSystemSize'

export const CustomerRFPSection: FunctionComponent<CustomerRFPSectionProps> = ({
  rfpDocumentSummary,
  isLast,
  searchString,
}: CustomerRFPSectionProps) => {
  const [toggleOpen, setToggleOpen] = useState(false)
  const customerRFPDashboardPath = `/document-exchange/projects/${rfpDocumentSummary.customerRFPId}`

  // expand the section if the user is searching
  const open = toggleOpen || !!searchString

  return (
    <>
      <TableRow
        data-testid="customerRFPSection"
        sx={
          isLast && !open
            ? { '& .MuiTableCell-root': { borderBottom: 'none' } }
            : undefined
        }
      >
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setToggleOpen(!toggleOpen)}>
              <ExpandMore
                sx={{
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </IconButton>
            <Link
              href={customerRFPDashboardPath}
              underline="hover"
              sx={{
                // Override the pink hover and focus color defined in index.css.
                '&:hover, &:focus': {
                  color: rucksack.palette.primary.main,
                },
              }}
            >
              <TextWithTooltip
                text={rfpDocumentSummary.customerRFPName}
                searchString={searchString}
              />
            </Link>
          </Box>
        </TableCell>
        <TableCell>
          <RequiredDocumentsProgressBar
            required={rfpDocumentSummary.required}
          />
        </TableCell>
        <TableCell sx={{ paddingLeft: '30px' }}>
          <ReviewsIcon
            verified={rfpDocumentSummary.verified}
            pendingReview={rfpDocumentSummary.pendingReview}
          />
        </TableCell>
        <TableCell>{formatSystemSize(rfpDocumentSummary.systemSize)}</TableCell>
        <TableCell sx={{ padding: '0 16px 0 6px' }}>
          <PodMemberButton
            constructionName={rfpDocumentSummary.constructionName}
            analystName={rfpDocumentSummary.analystName}
            driverName={rfpDocumentSummary.driverName}
            contractsName={rfpDocumentSummary.contractsName}
            searchString={searchString}
          />
        </TableCell>
        <TableCell>
          <DateAndTime date={rfpDocumentSummary.lastUpdated} />
        </TableCell>
        <TableCellWithDefault>
          {rfpDocumentSummary.updatedBy && (
            <TextWithTooltip
              text={rfpDocumentSummary.updatedBy}
              variant="body2"
            />
          )}
        </TableCellWithDefault>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={CUSTOMER_RFP_COLUMNS.length}
          sx={{ padding: 0, ...(!open && { border: 0 }) }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                paddingTop: 1,
                paddingBottom: 2,
                paddingX: SITE_RFP_TABLE_PADDING,
              }}
            >
              <SiteRFPTable
                siteRFPDocumentSummaries={rfpDocumentSummary.siteRFPs}
                nestedSiteRFPTableProps={{
                  open,
                  customerRFPDashboardPath,
                }}
                searchString={searchString}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export interface CustomerRFPSectionProps {
  rfpDocumentSummary: RFPDocumentSummary
  isLast?: boolean
  searchString?: string
}
